import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import { AddressResolutionSetup } from "@/components/non_registrant/address-resolution-setup";
import { BrowserExtensionsSetup } from "@/components/non_registrant/browser-extensions-setup";
import { DashboardAttomTaxAssessorStatus } from "@/components/non_registrant/dashboard-attom-tax-assessor-status";
import { DashboardOrganizations } from "@/components/non_registrant/dashboard-organizations";
import { DashboardRegistrants } from "@/components/non_registrant/dashboard-registrants";
import { DashboardUsers } from "@/components/non_registrant/dashboard-users";
import { HeraConfigurationForm } from "@/components/non_registrant/hera-configuration-form";
import { LicenseConfigurationPanel } from "@/components/non_registrant/license-configuration-panel";
import { OrganizationDetailsSheet } from "@/components/non_registrant/organization-details-sheet";
import { PartiesSetupSection } from "@/components/non_registrant/parties-setup-section";
import { RegistrantDetailsSheet } from "@/components/non_registrant/registrant-details-sheet";
import PageHeader from "@/components/page-header";
import {
  SETUP_PAGE_CHROME_CLASS,
  SETUP_PAGE_LIST_TAB_CLASS,
  SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS,
  SETUP_PAGE_SCROLL_TAB_CLASS,
  SETUP_PAGE_SHELL_CLASS,
  SETUP_PAGE_TABS_CLASS,
} from "@/components/portal/setup-page-layout";
import {
  SETUP_TABS_LIST_CLASS,
  SETUP_TAB_TRIGGER_CLASS,
  SetupPageTabBar,
} from "@/components/portal/setup-page-tab-bar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import { useLicenseTypeFilterOptions } from "@/hooks/use-license-type-filter-options";
import { useOperatorDashboardStats } from "@/hooks/use-operator-dashboard-stats";
import { DASHBOARD_STATS_TAB_PRELOAD_ALL } from "@/lib/dashboard-query-options";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import { getDashboardLink } from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import { useIsFetching } from "@tanstack/react-query";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const SETUP_TAB_VALUES = [
  "hera",
  "license-type",
  "hera-operators",
  "government-users",
  "registrants",
  "organizations",
  "parties",
  "attom-status",
  "address-resolution",
  "extensions",
] as const;

type SetupTabValue = (typeof SETUP_TAB_VALUES)[number];

function normalizeSetupTab(tab: string | undefined): SetupTabValue {
  let normalized = tab || "hera";
  if (normalized === "users") {
    normalized = "hera-operators";
  }
  if (normalized === "attom-tax-assessor-status") {
    normalized = "attom-status";
  }
  if (!SETUP_TAB_VALUES.includes(normalized as SetupTabValue)) {
    return "hera";
  }
  return normalized as SetupTabValue;
}

export const Route = createFileRoute("/operator/setup")({
  validateSearch: (search: Record<string, unknown>) => {
    const partiesTab = search.partiesTab;
    return {
      tab: normalizeSetupTab(search.tab as string | undefined),
      partiesTab:
        typeof partiesTab === "string" && partiesTab ? partiesTab : "parties",
    };
  },
  component: OperatorSetupPage,
});

// Tabs whose content is not affected by the jurisdiction / license type
// filters, so the filters are hidden while one of them is selected.
const FILTERLESS_TABS: readonly SetupTabValue[] = [
  "hera",
  "license-type",
  "parties",
  "address-resolution",
  "extensions",
];

const SETUP_TABS: ReadonlyArray<{ value: SetupTabValue; label: string }> = [
  { value: "hera", label: "HERA" },
  { value: "license-type", label: "License Type" },
  { value: "hera-operators", label: "Hera Operators" },
  { value: "government-users", label: "Government Users" },
  { value: "registrants", label: "Registrants" },
  { value: "organizations", label: "Organizations" },
  { value: "parties", label: "Parties" },
  { value: "attom-status", label: "ATTOM Status" },
  { value: "address-resolution", label: "Address Resolution" },
  { value: "extensions", label: "Extensions" },
];

export function OperatorSetupPage() {
  // AuthenticatedGuard already completed auth + API client setup before rendering.
  const isAuthSetupComplete = true;
  const { tab: searchTab, partiesTab: searchPartiesTab = "parties" } =
    Route.useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState<SetupTabValue>(
    normalizeSetupTab(searchTab),
  );

  const [selectedRegistrantId, setSelectedRegistrantId] = useState<
    string | null
  >(null);
  const [isRegistrantSheetOpen, setIsRegistrantSheetOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);
  const [isOrganizationSheetOpen, setIsOrganizationSheetOpen] = useState(false);

  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>(
    () => sessionStorage.get(SESSION_KEYS.OPERATOR_SELECTED_JURISDICTIONS, []),
  );
  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    () => sessionStorage.get(SESSION_KEYS.OPERATOR_SELECTED_LICENSE_TYPES, []),
  );

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.OPERATOR_SELECTED_JURISDICTIONS,
      selectedJurisdictions,
    );
  }, [selectedJurisdictions]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.OPERATOR_SELECTED_LICENSE_TYPES,
      selectedLicenseTypes,
    );
  }, [selectedLicenseTypes]);

  const { licenseTypeOptions } = useLicenseTypeFilterOptions();
  const { filteredJurisdictions, jurisdictionsData } =
    useOperatorDashboardStats(
      isAuthSetupComplete,
      selectedJurisdictions,
      DASHBOARD_STATS_TAB_PRELOAD_ALL,
      selectedLicenseTypes,
    );

  const jurisdictionOptions =
    filteredJurisdictions?.map((j) => ({
      value: j.id,
      label: jurisdictionOptionLabel(j),
    })) ?? [];
  const jurisdictionCatalog = useMemo(
    () =>
      (jurisdictionsData?.jurisdictions ?? []).map((jurisdiction) => ({
        id: jurisdiction.id,
        name: jurisdiction.name,
      })),
    [jurisdictionsData?.jurisdictions],
  );

  const effectiveJurisdictions = selectedJurisdictions;
  const showFilters = !FILTERLESS_TABS.includes(selectedTab);
  const attomTabActive = selectedTab === "attom-status";
  const attomIntegrationFetching = useIsFetching({
    queryKey: ["attom-integration-status"],
  });
  const attomStatusFetching = useIsFetching({
    queryKey: ["attom-tax-assessor-status"],
  });
  const attomTabLoading =
    attomTabActive && (attomIntegrationFetching > 0 || attomStatusFetching > 0);

  useEffect(() => {
    const normalized = normalizeSetupTab(searchTab);
    if (normalized !== selectedTab) {
      setSelectedTab(normalized);
    }
  }, [searchTab, selectedTab]);

  const handleTabChange = (value: string) => {
    const tab = normalizeSetupTab(value);
    setSelectedTab(tab);
    const currentPath = location.pathname;
    navigate({
      to: currentPath,
      search: { tab, partiesTab: searchPartiesTab },
      replace: true,
    });
  };

  // These lists render the page chrome inside their own scroller so the title
  // scrolls away with the rows, like the license list.
  const listHostsPageChrome =
    selectedTab === "parties" ||
    selectedTab === "hera-operators" ||
    selectedTab === "government-users" ||
    selectedTab === "registrants" ||
    selectedTab === "organizations";

  const pageChrome = (
    <>
      <PageHeader
        className={SETUP_PAGE_CHROME_CLASS}
        title={selectedTab === "parties" ? "Parties" : "Setup"}
        hideBackButton
        action={
          showFilters ? (
            <DashboardFilters
              selectedJurisdictions={selectedJurisdictions}
              onSelectedJurisdictionsChange={setSelectedJurisdictions}
              jurisdictionOptions={jurisdictionOptions}
              showJurisdictionFilter={true}
              selectedLicenseTypes={selectedLicenseTypes}
              onSelectedLicenseTypesChange={setSelectedLicenseTypes}
              licenseTypeOptions={licenseTypeOptions}
              showLicenseTypeFilter={true}
            />
          ) : undefined
        }
      />
      <p className="shrink-0 text-muted-foreground text-sm mt-2">
        {
          {
            hera: "Configure HERA system settings and integration.",
            "license-type":
              "Create and manage license types and their question groups.",
            "hera-operators": "Manage Hera operator user accounts.",
            "government-users":
              "Manage government user accounts for jurisdictions.",
            registrants:
              "View and manage registrant accounts (companies and individuals).",
            organizations:
              "View and manage organizations linked to registrants.",
            parties:
              "Manage party types used as selectable options when answering questions during registration, de-registration, and inspection, and clean up answers to company questions.",
            "attom-status":
              "Live Athena diagnostics for incremental ATTOM tax_assessor extract partitions.",
            "address-resolution":
              "Diagnose how an address geocodes and override the cached resolution.",
            extensions:
              "Install Hera Court Link so EXTENSION live links open court case search.",
          }[selectedTab]
        }
      </p>
    </>
  );

  return (
    <div className={SETUP_PAGE_SHELL_CLASS}>
      <Tabs
        value={selectedTab}
        onValueChange={handleTabChange}
        className={SETUP_PAGE_TABS_CLASS}
      >
        <SetupPageTabBar dashboardLink={getDashboardLink(UserType.OPERATOR)}>
          <TabsList className={SETUP_TABS_LIST_CLASS}>
            {SETUP_TABS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                className={SETUP_TAB_TRIGGER_CLASS}
                value={value}
              >
                {value === "attom-status" && attomTabLoading ? (
                  <Loader2
                    className="mr-1.5 h-3.5 w-3.5 animate-spin"
                    aria-hidden
                  />
                ) : null}
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </SetupPageTabBar>

        {listHostsPageChrome ? null : pageChrome}

        <TabsContent value="hera" className={SETUP_PAGE_SCROLL_TAB_CLASS}>
          <HeraConfigurationForm
            isActive={selectedTab === "hera"}
            onSaveSuccess={undefined}
            onCancel={undefined}
          />
        </TabsContent>

        <TabsContent value="license-type" className={SETUP_PAGE_LIST_TAB_CLASS}>
          <LicenseConfigurationPanel />
        </TabsContent>

        <TabsContent
          value="hera-operators"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardUsers
            userType={UserType.OPERATOR}
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            filterByUserType={UserType.OPERATOR}
            leadingContent={pageChrome}
          />
        </TabsContent>

        <TabsContent
          value="government-users"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardUsers
            userType={UserType.OPERATOR}
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            onSelectedJurisdictionsChange={setSelectedJurisdictions}
            jurisdictionCatalog={jurisdictionCatalog}
            filterByUserType={UserType.GOVERNMENT}
            showImpersonateButton
            leadingContent={pageChrome}
          />
        </TabsContent>

        <TabsContent
          value="registrants"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardRegistrants
            isAuthSetupComplete={isAuthSetupComplete}
            staffPortal={UserType.OPERATOR}
            showImpersonateButton
            leadingContent={pageChrome}
            selectedRegistrantId={selectedRegistrantId}
            onRegistrantSelect={(id) => {
              setSelectedRegistrantId(id);
              setIsRegistrantSheetOpen(true);
            }}
            isRegistrantSheetOpen={isRegistrantSheetOpen}
            onRegistrantSheetOpenChange={setIsRegistrantSheetOpen}
            onOpenOrganization={(orgId) => {
              setSelectedOrganizationId(orgId);
              setIsOrganizationSheetOpen(true);
              setIsRegistrantSheetOpen(false);
            }}
          />
        </TabsContent>

        <TabsContent
          value="organizations"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <DashboardOrganizations
            isAuthSetupComplete={isAuthSetupComplete}
            jurisdictionIds={effectiveJurisdictions}
            leadingContent={pageChrome}
            selectedOrganizationId={selectedOrganizationId}
            onOrganizationSelect={(id) => {
              setSelectedOrganizationId(id);
              setIsOrganizationSheetOpen(true);
            }}
            isOrganizationSheetOpen={isOrganizationSheetOpen}
            onOrganizationSheetOpenChange={setIsOrganizationSheetOpen}
            onOpenRegistrant={(registrantId) => {
              setSelectedRegistrantId(registrantId);
              setIsRegistrantSheetOpen(true);
              setIsOrganizationSheetOpen(false);
            }}
          />
        </TabsContent>

        <TabsContent
          value="parties"
          className={SETUP_PAGE_LIST_TAB_HOSTS_CHROME_CLASS}
        >
          <PartiesSetupSection
            isAuthSetupComplete={isAuthSetupComplete}
            showImpersonateButton
            helpPortal="operator"
            pageChrome={pageChrome}
            partiesTab={searchPartiesTab}
            onPartiesTabChange={(partiesTab) => {
              navigate({
                to: location.pathname,
                search: { tab: "parties", partiesTab },
                replace: true,
              });
            }}
          />
        </TabsContent>

        <TabsContent
          value="attom-status"
          className={SETUP_PAGE_SCROLL_TAB_CLASS}
        >
          <DashboardAttomTaxAssessorStatus
            isAuthSetupComplete={isAuthSetupComplete}
            isActive={attomTabActive}
            embedded
          />
        </TabsContent>

        <TabsContent
          value="address-resolution"
          className={SETUP_PAGE_SCROLL_TAB_CLASS}
        >
          <AddressResolutionSetup />
        </TabsContent>

        <TabsContent value="extensions" className={SETUP_PAGE_SCROLL_TAB_CLASS}>
          <BrowserExtensionsSetup showPublishNotes />
        </TabsContent>
      </Tabs>

      <RegistrantDetailsSheet
        isOpen={isRegistrantSheetOpen}
        onOpenChange={setIsRegistrantSheetOpen}
        registrantAccountId={selectedRegistrantId}
        onOpenOrganization={(orgId) => {
          setSelectedOrganizationId(orgId);
          setIsOrganizationSheetOpen(true);
          setIsRegistrantSheetOpen(false);
        }}
        key={selectedRegistrantId ?? "registrant-sheet"}
        showImpersonateButton
        staffPortal={UserType.OPERATOR}
      />
      <OrganizationDetailsSheet
        isOpen={isOrganizationSheetOpen}
        onOpenChange={setIsOrganizationSheetOpen}
        organizationId={selectedOrganizationId}
        onOpenRegistrant={(registrantId) => {
          setSelectedRegistrantId(registrantId);
          setIsRegistrantSheetOpen(true);
          setIsOrganizationSheetOpen(false);
        }}
        staffPortal={UserType.OPERATOR}
        key={selectedOrganizationId ?? "organization-sheet"}
      />
    </div>
  );
}
